import { createIntl as createFormatJsIntl, createIntlCache, IntlShape } from '@formatjs/intl'
import invariant from 'invariant'
import React, { use } from 'react'
import { RawIntlProvider } from 'react-intl'

export interface ValeyardIntlContextValue {
  intl: IntlShape
  messages: Record<string, any[]>
  allLayersLoaded: boolean
}

export const ValeyardIntlContext = React.createContext<ValeyardIntlContextValue | undefined>(undefined)

export const ValeyardIntlProvider = ({
  intl,
  messages,
  children,
  allLayersLoaded
}: ValeyardIntlContextValue & {
  children: React.ReactNode
}) => {
  return (
    // This ValeyardIntlContext give the intl and messages context to <Msg/> and useMsg().
    // And its the Provider that gives devs the intl object with additional formatting methods.
    <ValeyardIntlContext value={{ intl, messages, allLayersLoaded }}>
      {/* This RawIntlProvider gives the intl context to format.ts components like <FormattedDate/> */}
      <RawIntlProvider value={intl as IntlShape<any>}>{children}</RawIntlProvider>
    </ValeyardIntlContext>
  )
}

/**
 * @agent-offlimits useIntl context shim; use Msg or useMsg utils for message formatting
 */
export function useIntlContext() {
  const ctx = use(ValeyardIntlContext)
  invariant(ctx, 'useValeyardIntl must be used within a ValeyardIntlContext')
  return ctx
}

const intlCache = createIntlCache()

export function createIntl({ locale_tag, timezone }: { locale_tag: string | undefined; timezone: string | undefined }) {
  return createFormatJsIntl(
    {
      locale: locale_tag || 'en',
      timeZone: timezone,
      messages: {}
    },
    intlCache
  )
}
